import { lazy, Suspense, useCallback } from "react";
import Modal from "../../Components/Modal/Modal";
import { getKaminBrokerOrders, getKaminChances, getKaminGraph, KaminChanceLeg, KaminPage, KaminBrokerOrder } from "../../Requests/kamin2leg";
import KaminTable from "./KaminTable";
import { useKaminPolling } from "./useKaminPolling";
import { printPriceWithDecimalsForTableCell } from "../../helper";
import Cell from "../../Tables/TableTemplate/Cell";
import { toJalaliDateTime } from "../../Utils/date";
import Loading from "../../Components/Loading/Loading";
import KaminMonitoring from "./KaminMonitoring";

const Chart = lazy(() => import("../../Components/Timeseries/WrappedTimeSeriesChart").then(module => ({ default: module.WrappedTimeSeriesChart })));
const number = printPriceWithDecimalsForTableCell;
const date = (value?: string) => <Cell clonedRow={{ value: value ? toJalaliDateTime(value) || "-" : "-" }} headerName="Value" type="Normal" />;
const purpose = (value: string) => ({ Kamin: "کمین", Hunt: "شکار", Hedge: "رفع ناترازی" }[value] || value);
export type KaminReportKind = "chances" | "broker-orders" | "graph" | "monitoring";
export interface KaminRowReport { orderId: number; kind: KaminReportKind; }

type InstrumentName = (id: string | undefined, fallback: string) => string;
type AccountName = (id: number | undefined) => string;

function ReportTable({ orderId, kind, instrumentName, accountName }: { orderId: number; kind: "chances" | "broker-orders"; instrumentName: InstrumentName; accountName: AccountName }) {
  const load = useCallback((offset: number, pageSize: number, signal: AbortSignal): Promise<KaminPage<any>> => kind === "chances"
    ? getKaminChances(orderId, offset, pageSize, signal) : getKaminBrokerOrders(orderId, offset, pageSize, signal),
  [kind, orderId]);
  const chanceColumn = (title: string, render: (leg: KaminChanceLeg) => React.ReactNode) => ({ title,
    render: (_: any, row: { legs: KaminChanceLeg[] }) => row.legs.map(leg => <div key={leg.id}>{render(leg)}</div>) });
  const columns = kind === "chances" ? [
    { title: "زمان", dataIndex: "createdAt", render: date },
    chanceColumn("نماد", leg => leg.instrumentName), chanceColumn("نوع", leg => leg.isRefill ? "تکمیل حجم کمین (Refill)" : purpose(leg.purpose)),
    chanceColumn("قیمت", leg => number(leg.price)), chanceColumn("حجم", leg => number(leg.volume)),
    chanceColumn("انجام‌شده", leg => number(leg.filledVolume)), chanceColumn("وضعیت", leg => leg.status),
    chanceColumn("پیام", leg => leg.message || "—"),
  ] : [
    { title: "#", dataIndex: "id" }, { title: "اکانت", dataIndex: "sahraUserId", render: accountName },
    { title: "نماد", dataIndex: "instrumentId", render: (id: string) => instrumentName(id, id) },
    { title: "جهت", dataIndex: "side", render: (side: number) => side === 1 ? "خرید" : "فروش" },
    { title: "نوع", dataIndex: "purpose", render: (value: string, row: KaminBrokerOrder) => row.isRefill ? "تکمیل حجم کمین (Refill)" : value === "Hunt" ? row.huntTriggerId ? "شکار انجام کمین" : "شکار مستقل" : purpose(value) },
    { title: "تریگر", dataIndex: "huntTriggerId", render: number },
    { title: "درخواست", dataIndex: "requestType", render: (type: number) => ({ 1: "ثبت", 2: "ویرایش", 3: "لغو" }[type] || type) },
    { title: "فلگ لغو", dataIndex: "cancelRequested", render: (value?: boolean) => value == null ? "—" : value ? "بله" : "خیر" },
    { title: "وضعیت سفارش", dataIndex: "executionStatus", render: (value: string | undefined, row: KaminBrokerOrder) => row.isCancelled ? "لغوشده" :
      row.cancelRequested && value !== "Done" && value !== "Rejected" ? "فلگ لغو زده شده؛ منتظر تأیید لغو" : ({ Pending: "باز", Done: "انجام‌شده", Cancelled: "لغوشده", Rejected: "ردشده",
        Queued: "ارسال‌نشده", Unknown: "منتظر تأیید ارسال", AwaitingFill: "منتظر تأیید انجام" } as Record<string, string>)[value || ""] || value || "—" },
    { title: "قیمت", dataIndex: "price", render: number },
    { title: "قیمت هدف", dataIndex: "targetPrice", render: (value?: number | null) => value == null ? "—" : number(value) },
    { title: "حجم", dataIndex: "volume", render: number },
    { title: "انجام‌شده", dataIndex: "filledVolume", render: number }, { title: "سریال", dataIndex: "serialNumber" },
    { title: "زمان ایجاد", dataIndex: "insertDate", render: date }, { title: "زمان ارسال", dataIndex: "placeDate", render: date },
    { title: "زمان پاسخ", dataIndex: "responseDate", render: date },
    { title: "زمان آخرین آپدیت سفارش", dataIndex: "lastOrderUpdateTime", render: date },
    { title: "نتیجه درخواست", dataIndex: "success", render: (success?: boolean) => success == null ? "در انتظار پاسخ" : success ? "موفق" : "ناموفق" },
    { title: "پیام", dataIndex: "message" },
  ];
  return <KaminTable columns={columns} getPage={load} onSelect={() => {}} />;
}

function ExecutionGraph({ orderId }: { orderId: number }) {
  const load = useCallback((signal: AbortSignal) => getKaminGraph(orderId, signal), [orderId]);
  const { data } = useKaminPolling(load);
  return <div className="chart-fill-modal">
    {data ? <Suspense fallback={<Loading className="!text-sky-500" />}>
      <Chart dataset={data} className="h-full w-full" />
    </Suspense> : <Loading className="!text-sky-500" />}
  </div>;
}

export default function KaminRowReports({ report, onClose, instrumentName, accountName }: { report: KaminRowReport; onClose: () => void; instrumentName: InstrumentName; accountName: AccountName }) {
  const dockTitle = report.kind === "graph" ? "نمودار حجم انجام‌شده"
    : report.kind === "monitoring" ? "کمین"
      : report.kind === "chances" ? "شانس ها" : "سفارشات";
  return <Modal onClose={onClose} title={dockTitle} fullScreen={report.kind === "graph" || report.kind === "monitoring"}
    modalBoxClassName={report.kind === "chances" ? "!w-[900px] kamin-chances-modal" : undefined} UI={
    report.kind === "monitoring" ? <KaminMonitoring orderId={report.orderId} /> :
      report.kind === "graph" ? <ExecutionGraph orderId={report.orderId} /> : <ReportTable orderId={report.orderId} kind={report.kind} instrumentName={instrumentName} accountName={accountName} />
  } />;
}
